import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Alert,
} from "@mui/material";
import { IconRefresh } from "@tabler/icons-react";
import { AddButton } from "components/ui/buttons/StyledButtons";
import ContractForm from "components/contracts/ContractForm";
import { ContractEditorActions } from "components/contracts/ContractLoansPanel";
import ContractCalculations from "components/contracts/ContractCalculations";
import useContractPermissions from "hooks/useContractPermissions";
import {
  calculateDaysDifference,
  calculateInstallmentQuestionedValue,
  calculatePgto,
  calculateYearlyInterest,
} from "utils/contract-calculations";
import {
  allContractStatuses,
  buildNewContractDraft,
  contractCategories,
  normalizeOpenNewContractOptions,
  resolveContractId,
  STATUSES_ELIGIBLE_FOR_NON_ABUSIVE_PROMPT,
  todayIsoDate,
} from "utils/contract-editor";
import { formatDocument } from "utils/format-document";
import {
  formatRateAbusivenessMessage,
  isRateBelowAbusiveThreshold,
} from "utils/rate-abusiveness";
import {
  buildChildDraft,
  buildChildSavePayload,
  canLeaveLoanEditor,
  loanActionPermissions,
  loanSnapshot,
  mergePrincipalAfterRelatedSave,
  principalSnapshot,
  resolveCalculationTarget,
  shouldShowCalculationIdentity,
} from "utils/contract-loans";

const ContractEditorContext = createContext(null);

export function useContractEditor() {
  const context = useContext(ContractEditorContext);
  if (!context) {
    throw new Error("useContractEditor deve ser usado dentro de ContractEditorProvider");
  }
  return context;
}

export function ContractEditorProvider({ children }) {
  const apiRef = useRef(null);
  const openListenersRef = useRef(new Set());
  const api = React.useMemo(
    () => ({
      openNewContract: (options) => apiRef.current?.openNewContract(options),
      openContract: (contractOrId, options) => apiRef.current?.openContract(contractOrId, options),
      getCurrentContract: () => apiRef.current?.getCurrentContract() ?? null,
      preloadContractCatalogs: () => apiRef.current?.preloadContractCatalogs(),
      subscribeEditorOpen: (listener) => {
        openListenersRef.current.add(listener);
        listener(Boolean(apiRef.current?.isOpen?.()));
        return () => {
          openListenersRef.current.delete(listener);
        };
      },
    }),
    []
  );

  return (
    <ContractEditorContext.Provider value={api}>
      {children}
      <ContractEditorHost apiRef={apiRef} openListenersRef={openListenersRef} />
    </ContractEditorContext.Provider>
  );
}

function ContractEditorHost({ apiRef, openListenersRef }) {
  const { permissions, allowWithoutCalcFields } = useContractPermissions();
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [customerBanks, setCustomerBanks] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [salesConsultants, setSalesConsultants] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentContract, setCurrentContract] = useState(() => buildNewContractDraft(null));
  const [contractInstallments, setContractInstallments] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [loadingContract, setLoadingContract] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [lockCustomer, setLockCustomer] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [fieldsWithErrors, setFieldsWithErrors] = useState([]);
  const [loanSelection, setLoanSelection] = useState({ kind: "principal", childId: null });
  const [childSection, setChildSection] = useState("values");
  const [childContract, setChildContract] = useState(null);
  const [childInstallments, setChildInstallments] = useState([]);
  const [childBaseline, setChildBaseline] = useState("");
  const [principalBaseline, setPrincipalBaseline] = useState("");
  const [savingChild, setSavingChild] = useState(false);
  const childIdentityRef = useRef(null);
  const onSavedRef = useRef(null);
  const catalogsRequestedRef = useRef(false);
  const openingRef = useRef(false);

  const calculationTarget = resolveCalculationTarget(
    loanSelection,
    currentContract,
    contractInstallments,
    childContract,
    childInstallments
  );
  childIdentityRef.current = childContract ? (childContract.id ?? "new") : null;
  const calculationContract = calculationTarget.contract || currentContract;
  const calculationInstallments = calculationTarget.installments;
  const originalContractTotal = calculationContract.installments * calculationContract.installment_value;
  const firstInstallmentValue = Array.isArray(calculationInstallments) && calculationInstallments.length > 0
    ? calculationInstallments[0].installment_value ?? 0
    : 0;
  const interestMarketTotal = (firstInstallmentValue * calculationContract.installments) + calculationContract.contract_expenses_value;
  const overchargedAmountTotal = (originalContractTotal - interestMarketTotal);
  const paidTotal = (calculationContract.installments_payed * calculationContract.installment_value) + calculationContract.renegotiation_value;
  const overpaidAmountTotal = paidTotal > interestMarketTotal
    ? paidTotal - interestMarketTotal
    : interestMarketTotal - paidTotal;
  const isOverpaid = paidTotal > overpaidAmountTotal;
  const captionCalcOverpaidAmountTotal = isOverpaid ? "E = D - B" : "E = B - D";
  const captionOverpaidAmoutTotal = isOverpaid ? "PAGO A MAIOR" : "SALDO A PAGAR";
  const captionLegalInterestToptal = isOverpaid ? "JUROS LEGAIS" : "QUANT. PARCELAS RESTANTES";
  const legalInterestTotal = isOverpaid ? calculationContract.total_legal_interest : calculationContract.installments - calculationContract.installments_payed;
  const captionIgpmTotal = isOverpaid ? "IGPM" : "";
  const igpmTotal = 0;
  const captionCalcReturnTotal = isOverpaid ? "H = E + F + G" : "H = E / F";
  const captionReturnTotal = isOverpaid ? "A RESTITUIR" : "NOVA PARCELA";
  const returnTotal = isOverpaid ? (overpaidAmountTotal + legalInterestTotal + igpmTotal) : overpaidAmountTotal / legalInterestTotal;

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch("/backend/customers/");
      const data = await response.json();
      setCustomers(data.results);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    }
  }, []);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await fetch("/backend/banks/?page_size=1000");
      const data = await response.json();
      setBanks(data.results);
    } catch (error) {
      console.error("Erro ao buscar bancos:", error);
    }
  }, []);

  const fetchCustomerBanks = useCallback(async (customerId) => {
    if (!customerId) {
      setCustomerBanks([]);
      return;
    }
    try {
      const response = await fetch(`/backend/customer-banks/?customer_id=${customerId}`);
      if (response.ok) {
        const data = await response.json();
        const banksFromCustomer = (data.results || []).map((cb) => {
          const bankId = typeof cb.bank === "object" ? cb.bank.id : cb.bank;
          const bankName = cb.bank_name || banks.find((b) => b.id === bankId)?.name || "";
          return {
            id: bankId,
            name: bankName,
          };
        }).filter((b) => b.id && b.name);
        setCustomerBanks(banksFromCustomer);
      } else {
        setCustomerBanks([]);
      }
    } catch (error) {
      console.error("Erro ao buscar bancos do cliente:", error);
      setCustomerBanks([]);
    }
  }, [banks]);

  const getOrganizedBanks = useCallback(() => {
    if (customerBanks.length === 0) {
      return banks;
    }

    const customerBankIds = new Set(customerBanks.map((cb) => cb.id));
    const customerBanksList = customerBanks.map((cb) => {
      const fullBank = banks.find((b) => b.id === cb.id);
      return fullBank || cb;
    });
    const otherBanks = banks.filter((b) => !customerBankIds.has(b.id));
    return [...customerBanksList, ...otherBanks];
  }, [banks, customerBanks]);

  const fetchLawyers = useCallback(async () => {
    try {
      const response = await fetch("/backend/lawyers/?page_size=1000");
      const data = await response.json();
      setLawyers(data.results);
    } catch (error) {
      console.error("Erro ao buscar advogados:", error);
    }
  }, []);

  const fetchSalesConsultants = useCallback(async () => {
    try {
      const response = await fetch("/backend/sales_consultants/?page_size=1000");
      const data = await response.json();
      setSalesConsultants(data.results);
    } catch (error) {
      console.error("Erro ao buscar consultores de vendas:", error);
    }
  }, []);

  const fetchContractTypes = useCallback(async () => {
    try {
      const response = await fetch("/backend/contract-types/?active=true");
      const data = await response.json();
      setContractTypes(data.results);
    } catch (error) {
      console.error("Erro ao buscar tipos de contrato:", error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch("/backend/users-list/");
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : (data.results || []));
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
    }
  }, []);

  const preloadContractCatalogs = useCallback(() => {
    if (catalogsRequestedRef.current) return;
    catalogsRequestedRef.current = true;
    fetchCustomers();
    fetchBanks();
    fetchContractTypes();
    fetchLawyers();
    fetchSalesConsultants();
    fetchUsers();
  }, [
    fetchCustomers,
    fetchBanks,
    fetchContractTypes,
    fetchLawyers,
    fetchSalesConsultants,
    fetchUsers,
  ]);

  useEffect(() => {
    if (banks.length > 0 && currentContract.customer?.id) {
      fetchCustomerBanks(currentContract.customer.id);
    }
  }, [banks, currentContract.customer?.id, fetchCustomerBanks]);

  useEffect(() => {
    if (!openModal) openingRef.current = false;
    openListenersRef.current.forEach((listener) => listener(openModal));
  }, [openModal, openListenersRef]);

  const beginOpen = () => {
    if (openingRef.current || openModal) return false;
    openingRef.current = true;
    return true;
  };

  const resetLoanSession = () => {
    setLoanSelection({ kind: "principal", childId: null });
    setChildContract(null);
    setChildInstallments([]);
    setChildBaseline("");
    setChildSection("values");
    setPrincipalBaseline("");
    setSavingChild(false);
    setTabIndex((current) => (typeof current === "string" ? 0 : current));
  };

  const childEditorDirty = () => (
    Boolean(childContract) && loanSnapshot(childContract, childInstallments) !== childBaseline
  );

  const rememberOnSaved = (options) => {
    onSavedRef.current = typeof options?.onSaved === "function" ? options.onSaved : null;
  };

  const hydrateCustomer = async (customerId) => {
    if (!customerId) return;
    try {
      const response = await fetch(`/backend/customers/${customerId}/`, {
        credentials: "include",
      });
      if (!response.ok) return;
      const fullCustomer = await response.json();
      setCurrentContract((prev) => {
        const currentId = typeof prev.customer === "object" ? prev.customer?.id : prev.customer;
        if (String(currentId) !== String(fullCustomer.id)) return prev;
        return { ...prev, customer: fullCustomer };
      });
    } catch (error) {
      console.error("Erro ao carregar cliente inicial do contrato:", error);
    }
  };

  const openNewContract = async (options = {}) => {
    if (!beginOpen()) return;
    const normalized = normalizeOpenNewContractOptions(options);
    preloadContractCatalogs();
    rememberOnSaved(normalized);
    setFieldsWithErrors([]);
    setLockCustomer(normalized.lockCustomer);

    const initialCustomer = normalized.customer
      || (normalized.customerId ? { id: normalized.customerId } : null);

    setCurrentContract(buildNewContractDraft(todayIsoDate(), initialCustomer));
    setContractInstallments([]);
    resetLoanSession();
    setIsEditing(false);
    setCustomerBanks([]);
    setOpenModal(true);

    if (initialCustomer?.id) {
      fetchCustomerBanks(initialCustomer.id);
      if (!initialCustomer.name) {
        hydrateCustomer(initialCustomer.id);
      }
    }
  };

  const openContract = async (contractOrId, options = {}) => {
    const contractId = resolveContractId(contractOrId);
    if (!contractId || !beginOpen()) return;

    preloadContractCatalogs();
    rememberOnSaved(options);
    setFieldsWithErrors([]);
    setLockCustomer(Boolean(options.lockCustomer));
    setIsEditing(true);
    setLoadingContract(true);
    setOpenModal(true);
    resetLoanSession();

    try {
      const response = await fetch(`/backend/contracts/${contractId}/`, {
        credentials: "include",
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Falha ao carregar o contrato");
      const fullContract = await response.json();
      if (fullContract.parent_contract) {
        const parentResponse = await fetch(`/backend/contracts/${fullContract.parent_contract}/`, {
          credentials: "include",
          cache: "no-store",
        });
        if (!parentResponse.ok) throw new Error("Falha ao carregar o processo principal");
        const parent = await parentResponse.json();
        const parentRows = Array.isArray(parent.contract_installments) ? parent.contract_installments : [];
        const childRows = Array.isArray(fullContract.contract_installments) ? fullContract.contract_installments : [];
        setCurrentContract({ ...parent, _originalStatus: parent.status });
        setContractInstallments(parentRows);
        setPrincipalBaseline(principalSnapshot(parent));
        setChildContract(fullContract);
        setChildInstallments(childRows);
        setChildBaseline(loanSnapshot(fullContract, childRows));
        setChildSection("values");
        setLoanSelection({ kind: "child", childId: fullContract.id });
        setTabIndex(`loan:${fullContract.id}`);
        if (parent.customer?.id) fetchCustomerBanks(parent.customer.id);
        return;
      }
      setCurrentContract({
        ...fullContract,
        _originalStatus: fullContract.status,
      });
      setContractInstallments(Array.isArray(fullContract.contract_installments) ? fullContract.contract_installments : []);
      setPrincipalBaseline(principalSnapshot(fullContract));
      if (fullContract.customer?.id) {
        fetchCustomerBanks(fullContract.customer.id);
      }
    } catch (error) {
      console.error("Erro ao carregar contrato:", error);
    } finally {
      setLoadingContract(false);
    }
  };

  const handleCloseModal = () => {
    setFieldsWithErrors([]);
    resetLoanSession();
    setOpenModal(false);
  };

  const handleCloseWithConfirmation = () => {
    if (window.confirm("Você tem certeza que deseja fechar? Os dados não salvos serão perdidos.")) {
      handleCloseModal();
    }
  };

  const handleInitialCalculation = async () => {
    const active = resolveCalculationTarget(
      loanSelection,
      currentContract,
      contractInstallments,
      childContract,
      childInstallments
    );
    if (!active.ready || !active.contract) {
      alert("Selecione um contrato na aba Valores para calcular.");
      setTabIndex(1);
      return;
    }
    const c = active.contract;
    const ownerKind = loanSelection.kind;
    const ownerId = active.ownerId;
    if (!allowWithoutCalcFields) {
      const missing_fields = [];
      const minDate = "2000-01-01";

      if (!c.number || !String(c.number).trim()) missing_fields.push("Nº contrato");
      if (!c.customer?.id && !c.customer) missing_fields.push("Contratante");
      if (!c.bank?.id && !c.bank) missing_fields.push("Banco");
      if (!c.date) missing_fields.push("Data do fechamento do contrato");
      else if (c.date < minDate) missing_fields.push("Data do fechamento do contrato (deve ser a partir de 01/01/2000)");
      if (!c.status) missing_fields.push("Situação");
      if (!c.state || !String(c.state).trim()) missing_fields.push("UF");
      if (!c.sales_consultant?.id && !c.sales_consultant_id) missing_fields.push("Consultor de vendas");
      if (c.contracted_value == null || c.contracted_value === 0) missing_fields.push("Valor contratado");
      if (c.installments == null || c.installments === 0) missing_fields.push("Parcelas");
      if (c.installment_value == null || c.installment_value === 0) missing_fields.push("Valor das parcelas");
      if (c.interest_rate_monthly_value == null || c.interest_rate_monthly_value === 0) missing_fields.push("Taxa de juros contratada A.M.");
      if (!c.contract_at) missing_fields.push("Data da contratação");
      else if (c.contract_at < minDate) missing_fields.push("Data da contratação (deve ser a partir de 01/01/2000)");
      if (!c.first_installment_at) missing_fields.push("Data primeira parcela");
      else if (c.first_installment_at < minDate) missing_fields.push("Data primeira parcela (deve ser a partir de 01/01/2000)");
      if (c.installments_payed == null || c.installments_payed <= 0) missing_fields.push("Parcelas pagas (deve ser maior que zero)");
      if (!c.contract_type?.id && !c.contract_type_id) missing_fields.push("Tipo de contrato");
      if (!c.updated_at) missing_fields.push("Atualizado até");
      else if (c.updated_at < minDate) missing_fields.push("Atualizado até (deve ser a partir de 01/01/2000)");
      if (c.filed_at && c.filed_at < minDate) missing_fields.push("Ajuizado em (deve ser a partir de 01/01/2000)");
      if (c.file_at && c.file_at < minDate) missing_fields.push("Ajuizar em (deve ser a partir de 01/01/2000)");

      if (missing_fields.length > 0) {
        setFieldsWithErrors(missing_fields);
        const tab1Fields = ["Valor contratado", "Parcelas", "Valor das parcelas", "Taxa de juros", "Data da contratação", "Data primeira parcela", "Parcelas pagas", "Tipo de contrato", "Atualizado até", "Taxa de juros média"];
        const tab0Fields = ["Nº contrato", "Contratante", "Banco", "Data do fechamento", "Situação", "UF", "Consultor de vendas", "Ajuizado em", "Ajuizar em"];
        const firstError = missing_fields[0];
        if (ownerKind === "child" || ownerKind === "new") {
          setChildSection("values");
          setTabIndex(c.id ? `loan:${c.id}` : "loan:new");
        } else if (firstError && tab1Fields.some((f) => firstError.startsWith(f))) setTabIndex(1);
        else if (firstError && tab0Fields.some((f) => firstError.startsWith(f))) setTabIndex(0);
        setTimeout(() => alert("⚠️ CAMPOS OBRIGATÓRIOS NÃO PREENCHIDOS:\n\nOs seguintes campos precisam ser preenchidos antes de calcular as parcelas:\n\n" + missing_fields.map((f, i) => `${i + 1}. ${f}`).join("\n")), 50);
        return;
      }
    }

    const payload = {
      installments: c.installments,
      contracted_value: c.contracted_value,
      installments_payed: c.installments_payed,
      renegotiation_installments: c.renegotiation_installments,
      interest_rate_average_monthly_value: c.interest_rate_average_monthly_value,
      first_installment_at: c.first_installment_at,
      contract_expenses_value: c.contract_expenses_value,
      installment_questioned_value: c.installment_questioned_value,
    };

    try {
      const response = await fetch("/backend/initial-calculation-installments/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = "";
        const requiredFieldsMissing = [];

        try {
          const errorData = await response.json();

          if (errorData.missing_fields && Array.isArray(errorData.missing_fields)) {
            requiredFieldsMissing.push(...errorData.missing_fields);
          }

          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === "string") {
            errorMessage = errorData;
          } else {
            const errorMessages = [];
            const fieldMapping = {
              installments: "Número de parcelas",
              contracted_value: "Valor contratado",
              installments_payed: "Parcelas pagas",
              renegotiation_installments: "Parcelas de renegociação",
              interest_rate_average_monthly_value: "Taxa de juros média A.M.",
              first_installment_at: "Data primeira parcela",
              contract_expenses_value: "Despesas do contrato",
              installment_questioned_value: "Valor da parcela questionada",
            };

            Object.keys(errorData).forEach((key) => {
              const fieldName = fieldMapping[key] || key;
              const fieldErrors = Array.isArray(errorData[key])
                ? errorData[key]
                : [errorData[key]];
              fieldErrors.forEach((error) => {
                errorMessages.push(`${fieldName}: ${error}`);
              });
              if (fieldMapping[key] && !requiredFieldsMissing.includes(fieldName)) {
                requiredFieldsMissing.push(fieldName);
              }
            });

            errorMessage = errorMessages.length > 0
              ? errorMessages.join("\n")
              : "Não foi possível calcular as parcelas. Verifique os dados informados.";
          }

          if (requiredFieldsMissing.length > 0) {
            setFieldsWithErrors(requiredFieldsMissing);
            setTabIndex(1);
            const msg = errorData?.detail || "⚠️ CAMPOS OBRIGATÓRIOS NÃO PREENCHIDOS:\n\n" + requiredFieldsMissing.map((f, i) => `${i + 1}. ${f}`).join("\n");
            setTimeout(() => alert(msg), 50);
            return;
          }
        } catch (parseError) {
          if (response.status === 400) {
            errorMessage = "Dados inválidos para calcular as parcelas. Verifique se todos os campos obrigatórios foram preenchidos corretamente.";
          } else if (response.status === 500) {
            errorMessage = "Erro interno do servidor ao calcular as parcelas. Tente novamente mais tarde ou entre em contato com o suporte.";
          } else {
            errorMessage = `Erro ao calcular parcelas (Status: ${response.status}). Verifique os dados e tente novamente.`;
          }
        }
        throw new Error(errorMessage || "Não foi possível calcular as parcelas.");
      }

      const data = await response.json();
      const totals = {
        total_amortization: data.totals.total_amortization,
        total_installment_value: data.totals.total_installment_value,
        total_overpaid_value: data.totals.total_overpaid_value,
        total_legal_interest: data.totals.total_legal_interest,
        total_correction_value: data.totals.total_correction_value,
      };
      if (ownerKind === "child" || ownerKind === "new") {
        setChildContract((prev) => {
          if (!prev || (prev.id ?? "new") !== ownerId) return prev;
          return { ...prev, ...totals };
        });
        setChildInstallments((prev) => (
          childIdentityRef.current === ownerId ? data.installments : prev
        ));
      } else {
        setContractInstallments(data.installments);
        setCurrentContract((prev) => ({ ...prev, ...totals }));
      }
    } catch (error) {
      console.error("Erro ao calcular:", error);
      alert(`Erro ao calcular parcelas:\n\n${error.message || "Erro desconhecido. Verifique os dados e tente novamente."}`);
    }
  };

  const handleSave = async () => {
    if (childEditorDirty()) {
      alert("Salve ou cancele o contrato adicional antes de salvar o processo. As alterações dele ainda não foram gravadas.");
      if (childContract?.id) {
        setLoanSelection({ kind: "child", childId: childContract.id });
        setTabIndex(`loan:${childContract.id}`);
      } else {
        setLoanSelection({ kind: "new", childId: null });
        setTabIndex("loan:new");
      }
      return;
    }
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/contracts/${currentContract.id}/`
      : "/backend/contracts/";

    const statusesRequiringMotivo = new Set(["CANCELADO", "FALTAM_DOCUMENTOS"]);
    const isStatusTransitionRequiringMotivo =
      statusesRequiringMotivo.has(currentContract.status) &&
      (!isEditing || currentContract._originalStatus !== currentContract.status);

    if (isStatusTransitionRequiringMotivo) {
      const missing = [];
      if (!currentContract.lawyer?.id) missing.push("Advogado");
      if (!String(currentContract.process_protocol_obs || "").trim()) {
        missing.push("Observação do protocolo do processo");
      }
      if (missing.length) {
        setFieldsWithErrors(missing);
        alert(`Preencha os campos obrigatórios:\n\n• ${missing.join("\n• ")}`);
        return;
      }
    }

    const payload = {
      ...currentContract,
      customer_id: currentContract.customer?.id || currentContract.customer,
      bank_id: currentContract.bank?.id || currentContract.bank,
      contract_type_id:
        currentContract.contract_type?.id || currentContract.contract_type,
      lawyer_id: currentContract.lawyer?.id || null,
      lawyer_wallet_id: currentContract.lawyer_wallet?.id || null,
      sales_consultant_id: currentContract.sales_consultant?.id || null,
      responsible_user_id: currentContract.responsible_user?.id || null,
      contract_installments: contractInstallments,
    };

    delete payload._originalStatus;

    if (
      STATUSES_ELIGIBLE_FOR_NON_ABUSIVE_PROMPT.has(payload.status) &&
      isRateBelowAbusiveThreshold(
        currentContract.interest_rate_monthly_value,
        currentContract.interest_rate_average_monthly_value,
        currentContract.category
      )
    ) {
      const changeStatus = window.confirm(
        `${formatRateAbusivenessMessage(
          currentContract.interest_rate_monthly_value,
          currentContract.interest_rate_average_monthly_value
        )}\n\nDeseja alterar a situação para NÃO POSSUI ABUSIVIDADE?`
      );
      if (changeStatus) {
        payload.status = "NAO_POSSUI_ABUSIVIDADE";
      }
    }

    try {
      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (response.ok) {
        let savedContract = null;
        try {
          savedContract = await response.json();
        } catch (parseError) {
          savedContract = null;
        }
        setFieldsWithErrors([]);
        alert("Contrato salvo com sucesso!");
        setOpenModal(false);
        if (onSavedRef.current) {
          try {
            await onSavedRef.current(savedContract);
          } catch (fetchError) {
            console.error("Erro ao recarregar lista após salvar:", fetchError);
            alert(`Contrato salvo, mas não foi possível atualizar a lista automaticamente.\n\nDetalhe: ${fetchError.message}\n\nRecarregue a página manualmente.`);
          }
        }
        return;
      }

      let errorMessage = "";
      const requiredFieldsMissing = [];
      const otherErrors = [];
      let errorData = null;

      try {
        errorData = await response.json();

        if (errorData.missing_fields && Array.isArray(errorData.missing_fields)) {
          requiredFieldsMissing.push(...errorData.missing_fields);
        }

        const fieldMapping = {
          customer_id: "Cliente",
          bank_id: "Banco",
          contract_type_id: "Tipo de contrato",
          lawyer_id: "Advogado",
          process_protocol_obs: "Observação do protocolo do processo",
          lawyer_wallet_id: "Carteira de advogado",
          sales_consultant_id: "Consultor de vendas",
          number: "Número do contrato",
          date: "Data do fechamento do contrato",
          category: "Categoria",
          status: "Status",
          contracted_value: "Valor contratado",
          installments: "Número de parcelas",
          installment_value: "Valor da parcela",
          contract_expenses_value: "Despesas do contrato",
          contract_at: "Data do contrato",
          first_installment_at: "Data da primeira parcela",
          contract_installments: "Parcelas",
          process_number: "Número do processo",
          complaint_protocol_number: "Número do protocolo de reclamação",
          state: "Estado",
          filed_at: "Data de ajuizamento",
          file_at: "Data de arquivamento",
          installment_questioned_value: "Valor da parcela questionada",
          interest_rate_monthly_value: "Taxa de juros mensal",
          interest_rate_yearly_value: "Taxa de juros anual",
          grace_period_days: "Carência",
          installments_payed: "Parcelas pagas",
          is_renegotiation: "Renegociação",
          renegotiation_installments: "Parcelas renegociadas",
          renegotiation_value: "Valor da renegociação",
          interest_rate_average_monthly_value: "Taxa de juros média mensal",
          interest_rate_average_yearly_value: "Taxa de juros média anual",
        };

        const requiredFields = [
          "customer_id",
          "bank_id",
          "contract_type_id",
          "contracted_value",
          "installments",
          "installment_value",
          "installment_questioned_value",
          "interest_rate_monthly_value",
          "interest_rate_yearly_value",
          "date",
          "contract_at",
          "first_installment_at",
          "grace_period_days",
          "installments_payed",
          "is_renegotiation",
          "renegotiation_installments",
          "interest_rate_average_monthly_value",
          "interest_rate_average_yearly_value",
        ];

        Object.keys(fieldMapping).forEach((backendField) => {
          if (errorData[backendField]) {
            const fieldName = fieldMapping[backendField];
            const fieldErrors = Array.isArray(errorData[backendField])
              ? errorData[backendField]
              : [errorData[backendField]];

            const isRequiredField = requiredFields.includes(backendField);

            if (isRequiredField) {
              let hasValidationError = false;

              fieldErrors.forEach((error) => {
                const errorText = String(error).toLowerCase();
                const isValidationError = errorText.includes("não encontrado") ||
                                         errorText.includes("not found") ||
                                         errorText.includes("inválido") ||
                                         errorText.includes("invalid") ||
                                         errorText.includes("já existe") ||
                                         errorText.includes("already exists");

                if (isValidationError) {
                  hasValidationError = true;
                  otherErrors.push(`${fieldName}: ${error}`);
                }
              });

              if (!hasValidationError) {
                if (!requiredFieldsMissing.includes(fieldName)) {
                  requiredFieldsMissing.push(fieldName);
                }
              }
            } else {
              fieldErrors.forEach((error) => {
                otherErrors.push(`${fieldName}: ${error}`);
              });
            }
          }
        });

        if (requiredFieldsMissing.length === 0 && errorData && Object.keys(errorData).length > 0) {
          requiredFields.forEach((backendField) => {
            const fieldName = fieldMapping[backendField];
            if (fieldName && !requiredFieldsMissing.includes(fieldName)) {
              const fieldValue = payload[backendField];
              const isMissing = fieldValue === null ||
                               fieldValue === undefined ||
                               fieldValue === "" ||
                               (backendField.includes("_id") && (fieldValue === 0 || fieldValue === null));

              if (isMissing && !errorData[backendField]) {
                if (!requiredFieldsMissing.includes(fieldName)) {
                  requiredFieldsMissing.push(fieldName);
                }
              }
            }
          });
        }

        if (errorData.error && !errorData.error.includes && typeof errorData.error === "string") {
          const errorLower = errorData.error.toLowerCase();
          if (!errorLower.includes("obrigatório") && !errorLower.includes("required")) {
            otherErrors.push(errorData.error);
          }
        } else if (errorData.non_field_errors) {
          const nonFieldErrors = Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors
            : [errorData.non_field_errors];
          nonFieldErrors.forEach((error) => {
            const errorLower = String(error).toLowerCase();
            if (!errorLower.includes("obrigatório") && !errorLower.includes("required")) {
              otherErrors.push(String(error));
            }
          });
        } else if (errorData.detail) {
          const errorLower = String(errorData.detail).toLowerCase();
          if (!errorLower.includes("obrigatório") && !errorLower.includes("required")) {
            otherErrors.push(String(errorData.detail));
          }
        }

        if (requiredFieldsMissing.length === 0 && otherErrors.length === 0) {
          Object.keys(errorData).forEach((key) => {
            if (!fieldMapping[key] && key !== "error" && key !== "non_field_errors" && key !== "detail") {
              const fieldErrors = Array.isArray(errorData[key])
                ? errorData[key]
                : [errorData[key]];
              fieldErrors.forEach((error) => {
                otherErrors.push(`${key}: ${error}`);
              });
            }
          });
        }
      } catch (parseError) {
        if (response.status === 400) {
          otherErrors.push("Dados inválidos. Verifique os campos preenchidos.");
        } else if (response.status === 404) {
          otherErrors.push("Contrato não encontrado.");
        } else if (response.status === 500) {
          otherErrors.push("Erro interno do servidor. Tente novamente mais tarde.");
        } else {
          otherErrors.push(`Erro ao salvar contrato (Status: ${response.status})`);
        }
      }

      if (requiredFieldsMissing.length > 0 || otherErrors.length > 0) {
        const messageParts = [];

        if (requiredFieldsMissing.length > 0) {
          if (errorData?.missing_fields?.length && errorData?.detail) {
            messageParts.push(errorData.detail);
            messageParts.push("");
          }
          messageParts.push("⚠️ CAMPOS OBRIGATÓRIOS NÃO PREENCHIDOS:");
          messageParts.push("");
          requiredFieldsMissing.forEach((field, idx) => {
            messageParts.push(`${idx + 1}. ${field}`);
          });
        }

        if (otherErrors.length > 0) {
          if (requiredFieldsMissing.length > 0) {
            messageParts.push("");
            messageParts.push("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
            messageParts.push("");
          }
          messageParts.push("❌ OUTROS ERROS:");
          messageParts.push("");
          otherErrors.forEach((error, idx) => {
            messageParts.push(`${idx + 1}. ${error}`);
          });
        }

        errorMessage = messageParts.join("\n");
        setFieldsWithErrors(requiredFieldsMissing);
        const tabOrder = ["Nº contrato", "Número do contrato", "Contratante", "Cliente", "Banco", "Data do fechamento do contrato", "Data do contrato", "Situação", "Status", "UF", "Estado", "Consultor de vendas", "Ajuizado em", "Data de ajuizamento", "Data de protocolo", "Ajuizar em", "Data de arquivamento"];
        const tab1Fields = ["Valor contratado", "Parcelas", "Número de parcelas", "Valor das parcelas", "Valor da parcela", "Taxa de juros contratada", "Taxa de juros mensal", "Data da contratação", "Data primeira parcela", "Data da primeira parcela", "Parcelas pagas", "Tipo de contrato", "Atualizado até"];
        const firstError = requiredFieldsMissing[0];
        if (firstError && tab1Fields.some((f) => firstError.startsWith(f))) {
          setTabIndex(1);
        } else if (firstError && tabOrder.some((f) => firstError.startsWith(f))) {
          setTabIndex(0);
        }
      } else {
        errorMessage = "Não foi possível salvar o contrato. Verifique os dados e tente novamente.";
        setFieldsWithErrors([]);
      }

      setTimeout(() => alert(errorMessage), 50);
    } catch (error) {
      console.error("Erro ao salvar contrato:", error);
      alert(`Erro ao salvar contrato: ${error.message || "Erro de conexão. Verifique sua internet e tente novamente."}`);
    }
  };

  const setInterestRateAverageMonthlyValue = async (contract_type_id, contract_at, applyTo = null) => {
    try {
      if (
        !contract_type_id ||
        !contract_at ||
        isNaN(new Date(contract_at).getTime())
      ) {
        return;
      }

      const params = new URLSearchParams({
        id: contract_type_id,
        date: contract_at,
      });

      const response = await fetch(
        `/backend/contract-type-interest-rate-month/?${params.toString()}`
      );

      if (!response.ok) {
        let errorMessage = "";

        try {
          const errorData = await response.json();

          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === "string") {
            errorMessage = errorData;
          } else {
            errorMessage = "Não foi possível buscar a taxa de juros para o tipo de contrato e data informados.";
          }
        } catch (parseError) {
          if (response.status === 404) {
            errorMessage = "Taxa de juros não encontrada para o tipo de contrato e data informados. Verifique se a taxa foi cadastrada para esta data.";
          } else if (response.status === 400) {
            errorMessage = "Dados inválidos para buscar a taxa de juros. Verifique o tipo de contrato e a data informados.";
          } else if (response.status === 500) {
            errorMessage = "Erro interno do servidor ao buscar a taxa de juros. Tente novamente mais tarde.";
          } else {
            errorMessage = `Erro ao buscar taxa de juros (Status: ${response.status}). Tente novamente.`;
          }
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.interest_rate === undefined || data.interest_rate === null) {
        throw new Error("A resposta do servidor não contém a taxa de juros. Verifique se a taxa foi cadastrada para esta data.");
      }

      const applyRate = applyTo || setCurrentContract;
      applyRate((prev) => ({
        ...prev,
        interest_rate_average_monthly_value: data.interest_rate,
        interest_rate_average_yearly_value: parseFloat(
          calculateYearlyInterest(data.interest_rate)
        ),
      }));
    } catch (error) {
      console.error("Erro ao buscar percentual:", error);
      alert(`Erro ao buscar taxa de juros:\n\n${error.message || "Erro desconhecido. Verifique os dados e tente novamente."}`);
    }
  };

  const clearChildEditor = () => {
    setChildContract(null);
    setChildInstallments([]);
    setChildBaseline("");
    setChildSection("values");
    setLoanSelection({ kind: "principal", childId: null });
  };

  const refreshPrincipalAfterChildChange = async () => {
    const response = await fetch(`/backend/contracts/${currentContract.id}/`, {
      credentials: "include",
      cache: "no-store",
    });
    if (!response.ok) throw new Error("Falha ao atualizar o processo");
    const remote = await response.json();
    const dirty = principalSnapshot(currentContract) !== principalBaseline;
    const merged = mergePrincipalAfterRelatedSave(currentContract, remote, principalBaseline);
    setCurrentContract(merged);
    if (!dirty) {
      setContractInstallments(Array.isArray(remote.contract_installments) ? remote.contract_installments : []);
      setPrincipalBaseline(principalSnapshot(remote));
    }
  };

  const showPrincipalSection = (index) => {
    setTabIndex(index);
    setLoanSelection({ kind: "principal", childId: null });
    return true;
  };

  const resumeDraft = () => {
    if (!childContract || childContract.id) return false;
    setLoanSelection({ kind: "new", childId: null });
    setTabIndex("loan:new");
    return true;
  };

  const selectChildLoan = async (childId) => {
    if (childContract?.id != null && Number(childContract.id) === Number(childId)) {
      setLoanSelection({ kind: "child", childId: childContract.id });
      setTabIndex(`loan:${childContract.id}`);
      return true;
    }
    if (!canLeaveLoanEditor(childEditorDirty(), window.confirm)) return false;
    setLoadingContract(true);
    try {
      const response = await fetch(`/backend/contracts/${childId}/`, {
        credentials: "include",
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Falha ao carregar o contrato");
      const full = await response.json();
      const rows = Array.isArray(full.contract_installments) ? full.contract_installments : [];
      setChildContract(full);
      setChildInstallments(rows);
      setChildBaseline(loanSnapshot(full, rows));
      setChildSection("values");
      setLoanSelection({ kind: "child", childId: full.id });
      setTabIndex(`loan:${full.id}`);
      return true;
    } catch (error) {
      console.error("Erro ao carregar contrato adicional:", error);
      alert("Não foi possível carregar o contrato adicional.");
      return false;
    } finally {
      setLoadingContract(false);
    }
  };

  const startNewChild = () => {
    if (!currentContract?.id) {
      alert("Salve o processo antes de adicionar outro contrato.");
      return false;
    }
    if (currentContract.parent_contract) return false;
    if (childContract && !childContract.id) {
      setLoanSelection({ kind: "new", childId: null });
      setTabIndex("loan:new");
      return true;
    }
    if (!canLeaveLoanEditor(childEditorDirty(), window.confirm)) return false;
    const draft = buildChildDraft(currentContract);
    setChildContract(draft);
    setChildInstallments([]);
    setChildBaseline(loanSnapshot(draft, []));
    setChildSection("values");
    setLoanSelection({ kind: "new", childId: null });
    setTabIndex("loan:new");
    return true;
  };

  const cancelChildLoan = () => {
    if (!canLeaveLoanEditor(childEditorDirty(), window.confirm)) return;
    clearChildEditor();
    setTabIndex(1);
  };

  const saveChildLoan = async () => {
    if (!childContract || !currentContract?.id) return;
    setSavingChild(true);
    const payload = buildChildSavePayload(childContract, childInstallments, currentContract);
    const method = childContract.id ? "PUT" : "POST";
    const url = childContract.id ? `/backend/contracts/${childContract.id}/` : "/backend/contracts/";
    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let message = "Não foi possível salvar o contrato adicional.";
        try {
          const errorData = await response.json();
          const firstField = Object.values(errorData).flat()[0];
          message = errorData.detail || firstField || message;
        } catch (parseError) {
          message = `Não foi possível salvar o contrato adicional (status ${response.status}).`;
        }
        alert(message);
        return;
      }
      let saved = null;
      try {
        saved = await response.json();
      } catch (parseError) {
        saved = null;
      }
      await refreshPrincipalAfterChildChange();
      if (saved?.id) {
        const rows = Array.isArray(saved.contract_installments) ? saved.contract_installments : childInstallments;
        setChildContract(saved);
        setChildInstallments(rows);
        setChildBaseline(loanSnapshot(saved, rows));
        setLoanSelection({ kind: "child", childId: saved.id });
        setTabIndex(`loan:${saved.id}`);
      } else {
        clearChildEditor();
        setTabIndex(1);
      }
      alert("Contrato salvo com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar contrato adicional:", error);
      alert(`Erro ao salvar o contrato adicional: ${error.message || "Erro de conexão."}`);
    } finally {
      setSavingChild(false);
    }
  };

  const deleteChildLoan = async (childId) => {
    if (!window.confirm("Excluir este contrato adicional? A exclusão é lógica e o processo principal será mantido.")) {
      return;
    }
    if (childContract && childContract.id !== childId && !canLeaveLoanEditor(childEditorDirty(), window.confirm)) {
      return;
    }
    try {
      const response = await fetch(`/backend/contracts/${childId}/`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        let message = "Não foi possível excluir o contrato adicional.";
        try {
          const errorData = await response.json();
          message = errorData.detail || message;
        } catch (parseError) {
          message = `Não foi possível excluir o contrato adicional (status ${response.status}).`;
        }
        alert(message);
        return;
      }
      await refreshPrincipalAfterChildChange();
      if (childContract?.id === childId) clearChildEditor();
      setTabIndex(1);
    } catch (error) {
      console.error("Erro ao excluir contrato adicional:", error);
      alert(`Erro ao excluir o contrato adicional: ${error.message || "Erro de conexão."}`);
    }
  };

  const loanEditor = {
    selection: loanSelection,
    childContract,
    setChildContract,
    permissions,
    savingChild,
    onStartNewChild: startNewChild,
    onShowPrincipal: showPrincipalSection,
    onSelectChild: selectChildLoan,
    onResumeDraft: resumeDraft,
    onCancelChild: cancelChildLoan,
    onSaveChild: saveChildLoan,
    onDeleteChild: deleteChildLoan,
    onLookupChildAverageRate: (contractTypeId, contractAt) => (
      setInterestRateAverageMonthlyValue(contractTypeId, contractAt, setChildContract)
    ),
  };

  apiRef.current = {
    openNewContract,
    openContract,
    getCurrentContract: () => currentContract,
    preloadContractCatalogs,
    isOpen: () => openModal,
  };

  const calculationBody = (
    <>
      {!calculationTarget.ready && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Selecione um contrato na aba Valores para visualizar ou calcular.
        </Alert>
      )}
      {calculationTarget.ready && (
        <>
          {shouldShowCalculationIdentity(currentContract, loanSelection) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {`Cálculo inicial do contrato ${calculationContract.number || "sem número"}. Os resultados abaixo pertencem somente a este contrato.`}
            </Alert>
          )}
          <AddButton onClick={handleInitialCalculation}>
            <IconRefresh stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Calcular parcelas
          </AddButton>
          <ContractCalculations
            contractInstallments={calculationInstallments}
            currentContract={calculationContract}
            originalContractTotal={originalContractTotal}
            interestMarketTotal={interestMarketTotal}
            overchargedAmountTotal={overchargedAmountTotal}
            paidTotal={paidTotal}
            overpaidAmountTotal={overpaidAmountTotal}
            isOverpaid={isOverpaid}
            captionCalcOverpaidAmountTotal={captionCalcOverpaidAmountTotal}
            captionOverpaidAmoutTotal={captionOverpaidAmoutTotal}
            captionLegalInterestToptal={captionLegalInterestToptal}
            legalInterestTotal={legalInterestTotal}
            captionIgpmTotal={captionIgpmTotal}
            igpmTotal={igpmTotal}
            captionCalcReturnTotal={captionCalcReturnTotal}
            captionReturnTotal={captionReturnTotal}
            returnTotal={returnTotal}
          />
        </>
      )}
    </>
  );

  return (
    <Dialog
      open={openModal}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        if (reason === "escapeKeyDown") {
          handleCloseWithConfirmation();
          return;
        }
        handleCloseModal();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isEditing ? "Editar Contrato" : "Adicionar Contrato"}
      </DialogTitle>
      {loadingContract && <LinearProgress />}
      <DialogContent>
        <Box>
          <ContractForm
            currentContract={currentContract}
            setCurrentContract={setCurrentContract}
            customers={customers}
            banks={getOrganizedBanks()}
            lawyers={lawyers}
            salesConsultants={salesConsultants}
            contractTypes={contractTypes}
            contractCategories={contractCategories}
            contractStatuses={allContractStatuses}
            users={users}
            tabIndex={tabIndex}
            setTabIndex={setTabIndex}
            formatDocument={formatDocument}
            calculatePgto={calculatePgto}
            calculateInstallmentQuestionedValue={calculateInstallmentQuestionedValue}
            calculateYearlyInterest={calculateYearlyInterest}
            calculateDaysDifference={calculateDaysDifference}
            setInterestRateAverageMonthlyValue={setInterestRateAverageMonthlyValue}
            onCustomerChange={fetchCustomerBanks}
            customerBankIds={customerBanks.map((cb) => cb.id)}
            permissions={permissions}
            isEditing={isEditing}
            fieldsWithErrors={fieldsWithErrors}
            lockCustomer={lockCustomer}
            loanEditor={loanEditor}
            childSection={childSection}
            onChildSection={setChildSection}
            calculationBody={typeof tabIndex === "string" ? calculationBody : null}
          />

          {tabIndex === 2 && calculationBody}
        </Box>
      </DialogContent>

      <DialogActions sx={typeof tabIndex === "string" ? { px: 3, justifyContent: "space-between" } : undefined}>
        <ContractEditorActions
          loanActive={typeof tabIndex === "string"}
          canEdit={loanActionPermissions(permissions, {
            isFileOnlyMode: isEditing && !permissions?.can_edit_contracts && permissions?.can_file_contracts,
          }).canEdit}
          canDelete={Boolean(childContract?.id) && loanActionPermissions(permissions, {
            isFileOnlyMode: isEditing && !permissions?.can_edit_contracts && permissions?.can_file_contracts,
          }).canDelete}
          saving={savingChild}
          onClose={handleCloseModal}
          onSavePrincipal={handleSave}
          onSaveChild={saveChildLoan}
          onCancelChild={cancelChildLoan}
          onDeleteChild={() => deleteChildLoan(childContract?.id)}
        />
      </DialogActions>
    </Dialog>
  );
}
